import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Action, compose } from 'redux';
import { PageHeader } from 'components/page-header';
import { DataList } from 'components/select-advanced';
import { Page } from 'components/page-container';
import { Button, ButtonType } from 'components/button/index';
import { getRevenueColumns } from './utils';
import { mapReasonCodesToOptions } from 'utilities/util';
import BillToPartyComponent from 'components/bill-to-party/BillToPartyComponent';
import { addCostAndRevenue } from 'actions/costRevenue';
import { IStore } from 'reducers/index';
import { ThunkDispatch } from 'redux-thunk';
import './EditBillToPartyAndVatPartner.css';
import { getEmptyCostRev } from '../job-costing/utils/CostAndRevenueHelpers';
import { targetFunctionality } from 'constants/targetFunctionality';
import { INickname } from 'reducers/nickname';
import { callApiV2Put } from 'utilities/apiCaller';
import API from 'constants/api';
import { ModalScreen } from 'components/modal-screen';
import { ModalType } from 'components/modal-screen/ModalType';

interface IProps {
    originalRowData: any;
    rowData: any;
    reasonCodes: any;
    history: any;
    addCostAndRevenue: (data: any, targetType: string) => Promise<void >;
    targetType?: string;
    idJob?: number;
    target?: string | number;
    editVatMode?: boolean;
    editVatOnlyMode?: boolean;
    vatPartners?: any[];
    vatPartnersOptions?: any[];
    vatPartnerReasonCodes?: any[];
    nickname: INickname;
}

interface IMapDispatchToProps {
	addCostAndRevenue: (data: any, targetType: string) => Promise<void>;
}

export const _EditBillToPartyAndVatPartner = (props: IProps) => {
    const {
        originalRowData,
        rowData,
        reasonCodes,
        targetType,
        idJob,
        target,
        editVatMode,
        editVatOnlyMode,
        vatPartnersOptions,
        vatPartnerReasonCodes,
        nickname,
    } = props;
    const columns = useMemo(() => getRevenueColumns(), []);
    const [rows, setRows] = useState<any[]>([]);
    const [icbError, setIcbError] = useState<{ [key: number]: boolean }>({});
    const [hasVatChanged, setHasVatChanged] = useState<boolean>(false);
    const [showIcChangeConfirm, setShowIcChangeConfirm] = useState<boolean>(false);

    const isMciFlow = useMemo(() => {
        return originalRowData?.some((row: any) => row.division === 'MCI') || false;
    }, [originalRowData]);
    // const [editedVatPartners, setEditedVatPartners] = useState<object[]>([]);
    
    useEffect(() => {
        if (rowData) {
            setRows(rowData.map((row: any) => ({ ...row })));
        }
    }, [rowData]);


    const renderCell = (row: any, accessor: string, index: number) => {
        // we need to find the orignal row data to compare changes by using the index since both originalRowData and rowData are arrays
        const originalRow = originalRowData ? originalRowData[index] : null;
        if (accessor === 'billToParty' && !editVatOnlyMode) {
            return (
                <BillToPartyComponent
                    isAdding={false}
                    billToParty={row[accessor]}
                    onUpdate={(key: any, value: any, label: string) => {
                        if (!isMciFlow && key === 'icbIndicator' && originalRow?.icbIndicator !== value) {
                            setIcbError(prev => ({ ...prev, [index]: true }));
                        }
                        if (!isMciFlow && key === 'icbIndicator' && originalRow?.icbIndicator === value) {
                            setIcbError(prev => ({ ...prev, [index]: false }));
                        }
                        setRows(prev => prev.map((r, i) => i === index ? { ...r, [key]: value } : r));
                    }}
                    errorList={[]}
                    isDisabled={false}
                    showLabel={false}
                />
            );
        } else if (accessor === 'billToParty' && editVatOnlyMode) {
            return row[accessor];
        } else if (accessor === 'reasonCode') {
            if (editVatOnlyMode) {
                return row[accessor];
            }
            return(
                <DataList
                    id="reasonCode"
                    className="col-20"
                    label=""
                    value={rows[index].reasonCode}
                    onChange={(e) => {
                        const updatedRows = [...rows];
                        updatedRows[index][accessor] = e;
                        setRows(updatedRows);
                    }}
                    hasError={false}
                    data={reasonCodes ? mapReasonCodesToOptions(reasonCodes) : []} 
                />
            );
        } else if (editVatMode && accessor === 'vatPartner') {
            return (
                <DataList
                    id="vatPartner"
                    className="col-20"
                    label=""
                    value={rows[index].vatPartner}
                    onChange={(e: any) => {
                        const updatedRows = [...rows];
                        updatedRows[index].vatPartner = e;
                        setRows(updatedRows);
                        setHasVatChanged(true);
                    }}
                    hasError={false}
                    data={vatPartnersOptions || []} 
                />
            );
        } else if (editVatMode && accessor === 'vatPartnerReasonCodeNo') {
            return (
                <DataList
                    id="vatPartnerReasonCodeNo"
                    className="col-20"
                    label=""
                    value={rows[index].vatPartnerReasonCodeNo}
                    onChange={(e: any) => {
                        const updatedRows = [...rows];
                        updatedRows[index].vatPartnerReasonCodeNo = e;
                        setRows(updatedRows);
                        setHasVatChanged(true);
                    }}
                    hasError={false}
                    data={vatPartnerReasonCodes || []} 
                />
            );
        } else {
            return row[accessor];
        }
    };

    const disableSubmit = useMemo(() => {
        return Object.values(icbError).some(error => error);
    }, [icbError]);

    const reasonCodeChange = useMemo(() => {
        if (editVatOnlyMode) {
            return false;
        }
        if (isMciFlow) {
            // MCI only needs one of the two reason codes populated per row
            return rows.some(row => !row.reasonCode && !row.vatPartnerReasonCodeNo);
        }
        return rows.some(row => row.reasonCode === null || row.reasonCode === undefined || row.reasonCode === '');
    }, [rows, editVatOnlyMode, isMciFlow]);

    const vatSelectionInvalid = useMemo(() => {
        if (!editVatMode || !rows.length) {
            return false;
        }
        return rows.some((row, index) => {
            const originalRow = originalRowData ? originalRowData[index] : null;
            // without the original row we can only enforce selection, not an actual change
            const isChanged = originalRow ? row.vatPartner !== originalRow.vatPartner : true;
            if (editVatOnlyMode) {
                // changing the VAT partner is the sole purpose of this screen, so require a
                // partner to be selected, actually changed, and given a reason
                return !row.vatPartner || !row.vatPartnerReasonCodeNo || !isChanged;
            }
            // editing bill to party alone is valid here, so only demand a reason from rows
            // whose VAT partner was actually changed
            if (isMciFlow) {
                // MCI allows either reason code to satisfy the requirement
                return isChanged && !row.vatPartnerReasonCodeNo && !row.reasonCode;
            }
            return isChanged && !row.vatPartnerReasonCodeNo;
        });
    }, [rows, originalRowData, editVatMode, editVatOnlyMode, isMciFlow]);

    const hasIcFlagChanged = useMemo(() => {
        if (!originalRowData || !rows.length) {
            return false;
        }
        return rows.some((row, index) => {
            const originalRow = originalRowData[index];
            return originalRow && row.icbIndicator !== originalRow.icbIndicator;
        });
    }, [rows, originalRowData]);

    const getIcChangeDescription = (): { from: string; to: string } => {
        for (let i = 0; i < rows.length; i++) {
            const originalRow = originalRowData?.[i];
            if (originalRow && rows[i].icbIndicator !== originalRow.icbIndicator) {
                return {
                    from: originalRow.icbIndicator === 'IC' ? 'IC' : 'Non-IC',
                    to: rows[i].icbIndicator === 'IC' ? 'IC' : 'Non-IC',
                };
            }
        }
        return { from: '', to: '' };
    };

    const generatePayload = () => {
        const modifiedRows = rows.map((row, index) => {
            return {
                ...row,
                // destination rows carry rateDetailId, never essoRateDetailId, so map it through
                // for getEmptyCostRev which reads essoRateDetailId as the ChargeDetailId source
                essoRateDetailId: targetType === 'origin' ? row.essoRateDetailId : row.rateDetailId,
                sourceType: targetFunctionality.FCR_ExportJob === target ? 'FCR' : (targetFunctionality.ETS === target ? 'ETS' : 'CBL'),
                distribution: 2,
                updatedBy: nickname.employeeId,
            };
        });
        return getEmptyCostRev('REV', idJob || 0, modifiedRows)
    };
    const generateVatPayload = () => {
        return {
            updatedBy: nickname.employeeId,
            vatPartners: rows.map((row) => {
                return {
                    // the selected rows carry rateDetailId, never issoRateDetailId, so read
                    // rateDetailId for the import payload as getVatPartnerData already does
                    [targetType === 'origin' ? 'essoRateDetailId' : 'issoRateDetailId']:
                        targetType === 'origin' ? row.essoRateDetailId : row.rateDetailId,
                    vatPartnerCode: row.vatPartner,
                    vatPartnerReasonCodeNo: row.vatPartnerReasonCodeNo,
                }
            }),
        };
    }; 
    const saveEditedData = async () => {
        await props.addCostAndRevenue(generatePayload(), targetType || 'origin');
        // MCI: AddCostAndRevenue already updates the VAT partner, so the separate VAT API call is skipped
        if (hasVatChanged && !isMciFlow) {
            const payload = generateVatPayload();
            const apiUrl = targetType === 'origin' ? API.VatPartners.updateVatPartnersEsso : API.VatPartners.updateVatPartnersIsso;
            await callApiV2Put(apiUrl, payload, {});
        }
        props.history.goBack();
    };

    const handleSaveClick = () => {
        if (isMciFlow && hasIcFlagChanged) {
            setShowIcChangeConfirm(true);
        } else {
            saveEditedData();
        }
    };
    return (
        <>
            <PageHeader backButton={true}>Edit {editVatOnlyMode ? 'VAT Partner' : (editVatMode ? 'Bill To Party and VAT Partner' : 'Bill To Party')}</PageHeader>
            <Page>
                <table className="bill-to-party-vat-partner-table">
                    <thead>
                        <tr>
                            {columns.map((col: any) => (
                                <th key={col.accessor} className={col.headerClassName}>{col.fieldHeader}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row: any, idx: any) => (
                            <tr key={idx}>
                                {columns.map((col: any) => (
                                    <td key={col.accessor} className={`column-${col.accessor}`}>
                                        {renderCell(row, col.accessor, idx)}
                                        {!isMciFlow && icbError[idx] && col.accessor === 'billToParty' && (
                                            <div className="search-error">Bill to Party Not Allowed to change between IC and Non-IC</div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div className="edit-vat-table-actions">
                    <Button
                        isDisabled={disableSubmit || reasonCodeChange || vatSelectionInvalid}
                        buttonType={ButtonType.Blue}
                        onClick={handleSaveClick}
                    >
                        Save
                    </Button>
                    <Button
                        buttonType={ButtonType.Transparent}
                        onClick={props.history.goBack}
                        type="reset"
                    >
                        Cancel
                    </Button>
                </div>
                <ModalScreen
                    title="Confirm IC Status Change"
                    modalType={ModalType.s()}
                    visible={showIcChangeConfirm}
                    closeModal={() => setShowIcChangeConfirm(false)}
                    secondaryButtonFunc={() => setShowIcChangeConfirm(false)}
                    primaryButtonTitle="Confirm"
                    primaryButtonFunc={() => {
                        setShowIcChangeConfirm(false);
                        saveEditedData();
                    }}
                    secondaryButtonTitle="Cancel"
                >
                    <p>
                        Are you sure you want to change this MCI charge item from <strong>{getIcChangeDescription().from}</strong> to <strong>{getIcChangeDescription().to}</strong>?
                    </p>
                    <p>
                        This action will delete the existing charge item and create a new charge item with the updated Bill To Party.
                    </p>
                </ModalScreen>
            </Page>
        </>
    );
};

const mapStateToProps = (state: IStore) => ({
    originalRowData: state.editBillToPartyAndVatPartner.originalRowData,
    rowData: state.editBillToPartyAndVatPartner.rowData,
    reasonCodes: state.mdm.reasonCodes,
    targetType: state.editBillToPartyAndVatPartner.targetType,
    idJob: state.editBillToPartyAndVatPartner.idJob,
    target: state.editBillToPartyAndVatPartner.target,
    editVatMode: state.editBillToPartyAndVatPartner.editVatMode,
    editVatOnlyMode: state.editBillToPartyAndVatPartner.editVatOnlyMode,
    vatPartners: state.vatPartnerEdit.vatPartners,
    vatPartnersOptions: state.vatPartnerEdit.vatPartnersOptions,
    vatPartnerReasonCodes: state.vatPartnerEdit.vatPartnerEditReasonSelectOptions,
    nickname: state.nickname.userNickName,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): IMapDispatchToProps => ({
    addCostAndRevenue: (data: any, targetType: string) => dispatch(addCostAndRevenue(data, targetType)),
});
const enhance = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(_EditBillToPartyAndVatPartner);